import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  // Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import '@fontsource/roboto';
import '@fontsource/merriweather';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
// import AnnouncementIcon from '@mui/icons-material/Announcement';

const MotionBox = motion(Box);

const Home = () => {
  // Replace with a more reliable crossfade implementation
  const [backgroundStates, setBackgroundStates] = useState([
    { index: 0, opacity: 1, zIndex: 2 },
    { index: 1, opacity: 0, zIndex: 1 },
  ]);

  // Array of background images to alternate between
  const backgroundImages = [
    'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/gallery/group.jpg)',
    'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/gallery/practise_session.jpg)',
  ];

  // Improved effect to create consistent slow crossfade
  useEffect(() => {
    let imageIndex = 0;
    let isTransitioning = false;

    const transitionImages = () => {
      if (isTransitioning) return; // Prevent multiple transitions at once

      isTransitioning = true;

      // Calculate next image index
      const nextIndex = (imageIndex + 1) % backgroundImages.length;
      imageIndex = nextIndex;

      // Get current layers by their role rather than opacity or z-index
      setBackgroundStates(prevStates => {
        // Make a deep copy to avoid reference issues
        const newStates = JSON.parse(JSON.stringify(prevStates));

        // Find top and bottom layers
        const hiddenLayer = newStates.find(state => state.zIndex === 1);

        // Update hidden layer to show next image
        hiddenLayer.index = nextIndex;

        // Return state unchanged - we'll do the transition in the next step
        return newStates;
      });

      // Force a small delay before changing opacity to ensure the new image is loaded
      setTimeout(() => {
        setBackgroundStates(prevStates => {
          // Create new array to trigger re-render
          return prevStates.map(state => {
            if (state.zIndex === 2) {
              // Current visible layer - fade out
              return { ...state, opacity: 0, zIndex: 1 };
            } else {
              // Hidden layer with next image - fade in and bring to front
              return { ...state, opacity: 1, zIndex: 2 };
            }
          });
        });

        // Reset transition lock after transition completes
        setTimeout(() => {
          isTransitioning = false;
        }, 3000); // Match transition duration
      }, 50);
    };

    // Start slideshow with transition
    const intervalId = setInterval(transitionImages, 7000);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [backgroundImages.length]);

  return (
    <>
      <Helmet>
        <title>Mirmire - Home</title>
        <meta name="description" content="Welcome to Mirmire Sporting Club" />
      </Helmet>

      {/* MAMC Announcement Banner */}
      {/* <Paper
        elevation={2}
        sx={{
          bgcolor: '#000000',
          borderBottom: `1px solid #FFD700`,
          position: 'relative',
          py: 0.8,
          px: { xs: 1, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, sm: 2 },
          zIndex: 10,
          borderRadius: 0,
        }}
      >
        <AnnouncementIcon
          sx={{
            color: '#FFD700',
            fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.2rem' },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: '#FFFFFF',
            fontWeight: 'medium',
            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
          }}
        >
          Registration for Manoj Aryal Memorial Cup (MAMC) 2025 is now open!
        </Typography>
        <Button
          component={RouterLink}
          to="/auth"
          variant="outlined"
          size="small"
          sx={{
            color: '#FFD700',
            borderColor: '#FFD700',
            '&:hover': {
              bgcolor: 'rgba(255, 215, 0, 0.1)',
              borderColor: '#FFD700',
            },
            fontSize: { xs: '0.6rem', sm: '0.7rem' },
            py: 0.2,
            px: { xs: 1, sm: 1.5 },
            minWidth: 'auto',
            ml: 1,
          }}
        >
          View
        </Button>
      </Paper> */}

      {/* Hero Section with layered background images for crossfade effect */}
      <Box
        sx={{
          bgcolor: '#000000',
          color: '#FFFFFF',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid #FFD700',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          minHeight: { xs: '60vh', md: '100vh' },
          transform: 'scale(1)',
          transition: 'transform 0.8s ease-in-out', // Slow, smooth transition for transform
          '&:hover': {
            transform: 'scale(1.02)', // Subtle zoom effect
          },
        }}
      >
        {/* Render both background images with different opacity for crossfade */}
        {backgroundStates.map((state, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: backgroundImages[state.index],
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: state.opacity,
              zIndex: state.zIndex,
              transition: 'opacity 3s ease-in-out, transform 0.8s ease', // Increased from 2s to 3s for slower fade
              transform: 'scale(1)',
              '.MuiBox-root:hover &': {
                transform: 'scale(1.01)', // Subtle internal zoom on hover
              },
            }}
          />
        ))}

        {/* Rest of the hero content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6} lg={8}>
              <MotionBox
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  gutterBottom
                  sx={{
                    color: '#FFD700',
                    fontWeight: 700,
                    fontFamily: 'Merriweather, serif',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                  }}
                >
                  Welcome to Mirmire Sporting Club
                </Typography>
                <Typography
                  sx={{
                    mb: 4,
                    color: '#FFFFFF',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                  }}
                >
                  Championing youth, culture, and competition — the Mirmire way.
                </Typography>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                sx={{
                  height: '100%',
                  backgroundImage:
                    'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/gallery/practise_session.jpg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                  transition: 'transform 0.5s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              >
                {/* Add hero image here */}
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          py: { xs: 8, md: 12 },
          borderBottom: '1px solid #FFD700',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '1000px',
            px: 2, // Small padding for mobile responsiveness
          }}
        >
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              color: '#FFD700',
              fontWeight: 700,
              fontSize: '2.5rem',
              mb: 4,
              fontFamily: 'Merriweather, serif',
            }}
          >
            ABOUT
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#000000',
              fontSize: '1.1rem',
              lineHeight: 1.6,
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            Mirmire Sporting Club was founded in 2023 by a group of like-minded Nepalese football
            enthusiasts in Finland. What began as a shared passion among friends quickly evolved
            into a growing club with over 50 active members.
          </Typography>
        </Box>
      </Box>

      {/* Latest News Section */}
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          py: { xs: 8, md: 12 },
          borderBottom: '1px solid #FFD700',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '1000px',
            px: 2, // Small padding for mobile responsiveness
          }}
        >
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              color: '#FFD700',
              fontWeight: 700,
              fontSize: '2.5rem',
              mb: 4,
              fontFamily: 'Merriweather, serif',
            }}
          >
            LATEST NEWS
          </Typography>

          <Box>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                color: '#000000',
                fontWeight: 600,
                fontSize: '1.3rem',
                fontFamily: 'Merriweather, serif',
              }}
            >
              Upcoming Tournaments
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#000000',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                fontFamily: 'Roboto, sans-serif',
                mb: 2,
              }}
            >
              Mirmire SC is looking forward to participate in the following tournaments:
            </Typography>
            <List>
              {[
                'Himalayan Cup Football tournament 2026',
                'Gaule Cup Football tournament 2026',
                'FC Gurkha Cup 2026',
                'MAMC 2026',
              ].map((tournament, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <SportsSoccerIcon sx={{ color: '#FFD700' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={tournament}
                    sx={{
                      color: '#000000',
                      fontSize: { xs: '1.1rem', md: '1.5rem' },
                      lineHeight: 1.6,
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Button
              sx={{
                color: '#000000',
                fontWeight: 600,
                pl: 0,
                '&:hover': {
                  color: '#FFD700',
                  bgcolor: 'transparent',
                },
              }}
            ></Button>
          </Box>
        </Box>
      </Box>

      {/* Features Grid Section */}
      <Box sx={{ bgcolor: '#FFFFFF', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {[
              {
                title: 'GALLERY',
                image: '/gallery/MSC_0394.JPG',
                path: '/gallery',
                description: 'View our photo collection',
              },
              {
                title: 'EVENTS',
                image: '/events/MAMC_Logo.png',
                path: '/events',
                description: 'Check out upcoming events',
              },
              {
                title: 'PLAYERS',
                image: '/gallery/DSC05629.jpg',
                path: '/players',
                description: 'Meet our team members',
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    bgcolor: '#FFFFFF',
                    boxShadow: 'none',
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 200,
                      bgcolor: '#E5E5E5',
                      mb: 2,
                      backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${item.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: '0 8px 8px rgba(0, 0, 0, 0.3)',
                    }}
                    component="img"
                    loading="lazy"
                  />
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: '#000000',
                        fontWeight: 700,
                        mb: 2,
                        fontFamily: 'Merriweather, serif',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 3,
                        color: '#666666',
                        fontFamily: 'Roboto, sans-serif',
                      }}
                    >
                      {item.description}
                    </Typography>
                    <Button
                      component={RouterLink}
                      to={item.path}
                      variant="contained"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      sx={{
                        bgcolor: '#000000',
                        color: '#FFFFFF',
                        mt: 'auto',
                        '&:hover': {
                          bgcolor: '#FFD700',
                          color: '#000000',
                        },
                      }}
                    >
                      View
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Home;
